import React, { useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import { Box, Button, Flex, Heading, HStack, Text, Tooltip, useDisclosure } from '@chakra-ui/react';
import { FormVersionStatus, PropertyRoute } from '@common/entities';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { NotificationStatus } from 'enums';

import { GlobeIcon, PencilIcon, SyncIcon, ViewIcon } from 'components/custom-icons';
import { ConfirmationModal } from 'components/modal/ConfirmationModal';
import notification from 'components/notification';
import { RouteLanguageOverridesModal } from 'components/smart-routes/RouteLanguageOverridesModal';
import { createRouteDraft, getPropertyRoutesOverridenByRoute, publishRouteVersion } from 'services';
import { NoConflictMessage, PropertyConflictWarning } from 'utils/propertyConflictWarning';

export function SmartRoutesTopActions({ data, refetch }: any) {
  const { name, versions } = data;
  const { routeId, versionId } = useParams();
  const navigate = useNavigate();
  const { trigger } = useFormContext();
  const queryClient = useQueryClient();

  const currentVersion = versions?.[0] || {};
  const isDraft = currentVersion?.status === FormVersionStatus.Draft;
  const isActive = currentVersion?.status === FormVersionStatus.Active;

  const [modalText, setModalText] = useState<React.ReactElement | string>('');
  const [apiResult, setApiResult] = useState<boolean | null>(null);

  const { isOpen, onOpen, onClose } = useDisclosure();
  const { isOpen: isDraftOpen, onOpen: onDraftOpen, onClose: onDraftClose } = useDisclosure();
  const {
    isOpen: isLanguageOverridesOpen,
    onOpen: onLanguageOverridesOpen,
    onClose: onLanguageOverridesClose,
  } = useDisclosure();

  // Publish a Route
  const { isPending: isPublishing, mutateAsync: publish } = useMutation({
    mutationFn: async () => {
      try {
        return await publishRouteVersion({
          routeId,
          versionId: currentVersion.id,
        });
      } catch (error: any) {
        throw new Error(error.message || 'Failed to publish route');
      }
    },
    onSuccess: () => {
      setApiResult(true);
      notification(
        'Published',
        'The route has been successfully published!',
        NotificationStatus.SUCCESS,
      );
      onClose();
      navigate('/');
    },
  });

  // Create a Draft
  const { isPending: isDrafting, mutateAsync: createDraft } = useMutation({
    mutationFn: async () =>
      createRouteDraft({
        routeId,
        versionId: currentVersion.id,
      }),
    onSuccess: (response) => {
      const newDraftVersionId = response.data?.versions?.[0]?.id;

      if (!newDraftVersionId) {
        notification('Error', 'Failed to create a draft version.', NotificationStatus.ERROR);
        return;
      }

      setApiResult(true);
      notification(
        'Draft Created',
        'A new draft version has been created.',
        NotificationStatus.SUCCESS,
      );

      setApiResult(null);
      onDraftClose();

      navigate(`/routes/route/${routeId}/${newDraftVersionId}`);
    },
    onError: (err: any) => {
      setApiResult(false);
      notification('Error', err.message || 'Failed to create a draft.', NotificationStatus.ERROR);
      setApiResult(null);
      onDraftClose();
    },
  });

  /**
   * Handles Publishing the Route
   */
  async function handlePublish() {
    if (!routeId || !currentVersion.id) {
      notification('Error', 'Missing route or version information.', NotificationStatus.ERROR);
      return;
    }

    const isValid = await trigger();
    if (!isValid) {
      notification(
        'Error',
        'Please select a next step for each option before proceeding.',
        NotificationStatus.ERROR,
      );
      return;
    }

    setApiResult(null);

    const propertyRoutesOverriden = await getPropertyRoutesOverridenByRoute(routeId);
    if (propertyRoutesOverriden?.data?.length) {
      const routes = propertyRoutesOverriden.data as PropertyRoute[];
      setModalText(<PropertyConflictWarning propertyRoutes={routes} action="published" />);
    } else {
      setModalText(NoConflictMessage('publish'));
    }

    onOpen();
  }

  /**
   * Handles Creating a Draft
   */
  async function handleCreateDraft() {
    if (!routeId || !currentVersion.id) {
      notification('Error', 'Missing route or version information.', NotificationStatus.ERROR);
      return;
    }
    setApiResult(null);
    setModalText('This will create a new draft based from this version.');
    onDraftOpen();
  }

  async function handleExit() {
    refetch();
    navigate('/');
  }

  const openPreview = async () => {
    await queryClient.invalidateQueries({ queryKey: ['smart-route', routeId] });
    navigate(`/routes/preview/${routeId}/${versionId}`);
  };

  return (
    <Box>
      <Flex py={7} px={5} justifyContent="space-between">
        <HStack spacing={35}>
          <Tooltip label={name} isDisabled={name?.length < 25}>
            <Heading size="2xl">{name}</Heading>
          </Tooltip>

          {currentVersion?.status && (
            <Text color="black.base" fontSize={11}>
              <SyncIcon boxSize={4} /> {currentVersion.status}&nbsp;
              {new Date(currentVersion.updatedDate).toLocaleString()}
            </Text>
          )}
        </HStack>

        <HStack spacing={4}>
          <Button
            leftIcon={<GlobeIcon boxSize={6} />}
            data-testid="language-overrides"
            onClick={onLanguageOverridesOpen}
            variant="secondary"
          >
            Translations
          </Button>
          <Button
            mr={2}
            leftIcon={<ViewIcon boxSize={6} />}
            onClick={openPreview}
            variant="secondary"
          >
            Preview
          </Button>
          {isActive && (
            <Button
              leftIcon={<PencilIcon boxSize={5} />}
              onClick={handleCreateDraft}
              variant="secondary"
            >
              Edit
            </Button>
          )}
          {isDraft && (
            <Button variant="action" size="lg" onClick={handlePublish} isLoading={isPublishing}>
              Publish
            </Button>
          )}

          <Button size="lg" onClick={handleExit} variant="secondary">
            Exit
          </Button>
        </HStack>
      </Flex>

      {/* Publish Confirmation Modal */}
      <ConfirmationModal
        isOpen={isOpen}
        onClose={onClose}
        message={modalText}
        actionLabel="Continue"
        actionColor="blue.700"
        size="lg"
        onSubmit={async () => {
          try {
            await publish();
          } catch (error: any) {
            setApiResult(false);
            notification(
              'Error',
              error.message || 'Failed to publish route',
              NotificationStatus.ERROR,
              10000,
              true,
            );
            onClose();
          }
        }}
        apiStatus={apiResult}
        isLoading={isPublishing}
      />

      {/* Draft Confirmation Modal */}
      <ConfirmationModal
        isOpen={isDraftOpen}
        onClose={onDraftClose}
        message={modalText}
        actionLabel="Continue"
        actionColor="blue.700"
        onSubmit={createDraft}
        apiStatus={apiResult}
        isLoading={isDrafting}
      />

      {/* Route translations modal */}
      {routeId && versionId && (
        <RouteLanguageOverridesModal
          isOpen={isLanguageOverridesOpen}
          onClose={onLanguageOverridesClose}
          routeId={routeId}
          versionId={versionId}
        />
      )}
    </Box>
  );
}

export default SmartRoutesTopActions;
